import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { PasswordInput } from '../components/ui/password-input';
import { Input } from '../components/ui/input';
import logoImage from '@/assets/prsnl-logo.svg';
import { toast } from 'sonner';
import { updatePassword, verifyRecoveryToken, resetPasswordForEmail } from '@/services/data/auth';
import { updateHasSignedUp } from '@/services/data/users';
import { useAuthStore } from '@/stores/authStore';
import { Loader2, ArrowLeft } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import type { Session } from '@supabase/supabase-js';
import { useSupabaseVerificationLink } from '@/hooks/useSupabaseVerificationLink';

export function ResetPasswordScreen() {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(true);
  const [isSessionValid, setIsSessionValid] = useState(false);
  const [verificationError, setVerificationError] = useState<string | null>(null);
  const [storedSession, setStoredSession] = useState<Session | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const { setSession } = useAuthStore();
  const { tokenHash, type: verificationType } = useSupabaseVerificationLink();
  const hasVerifiedRef = useRef(false);

  useEffect(() => {
    if (tokenHash && verificationType === 'recovery' && !hasVerifiedRef.current) {
      hasVerifiedRef.current = true;

      // Verify the OTP token directly instead of redirecting
      const verifyToken = async () => {
        const { success, data, error } = await verifyRecoveryToken(tokenHash);
        
        if (!success || error) {
          setVerificationError('Lien de réinitialisation invalide ou expiré. Veuillez demander un nouveau lien.');
          setIsVerifying(false);
          return;
        }
        
        if (data) {
          setStoredSession(data);
          setSession(data);
          setIsSessionValid(true);
          setIsVerifying(false);
          
          // Clean up the URL
          window.history.replaceState({}, '', '/reset-password');
        }
      };
      
      verifyToken();
      return;
    }
    
    // Listen for auth state changes - Supabase will process the URL fragment tokens
    // IMPORTANT: Do NOT use async callback to avoid deadlocks
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        // Only handle initial auth events, not password updates
        if ((event === 'PASSWORD_RECOVERY' || event === 'SIGNED_IN') && !isSessionValid) {
          if (session) {
            setStoredSession(session);
            setSession(session);
            setIsSessionValid(true);
            setIsVerifying(false);
          }
        }
      }
    );

    // Also check for existing session (in case tokens were already processed)
    const checkExistingSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (session) {
        setStoredSession(session);
        setSession(session);
        setIsSessionValid(true);
        setIsVerifying(false);
      } else {
        // Wait a bit for Supabase to process URL fragment tokens
        setTimeout(() => {
          setIsVerifying(prev => {
            // Only show error if still verifying after timeout
            if (prev) {
              setVerificationError('Lien de réinitialisation invalide ou expiré. Veuillez demander un nouveau lien.');
            }
            return false;
          });
        }, 3000);
      }
    };
    
    checkExistingSession();

    return () => {
      subscription.unsubscribe();
    };
  }, [setSession, tokenHash, verificationType]);

  const validatePassword = (pwd: string): string | null => {
    if (pwd.length < 8) return 'Le mot de passe doit contenir au moins 8 caractères';
    if (!/[A-Z]/.test(pwd)) return 'Le mot de passe doit contenir au moins une majuscule';
    if (!/[a-z]/.test(pwd)) return 'Le mot de passe doit contenir au moins une minuscule';
    if (!/[0-9]/.test(pwd)) return 'Le mot de passe doit contenir au moins un chiffre';
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    // Use the stored session instead of making a new getSession call
    if (!storedSession) {
      setFormError('Votre session a expiré. Veuillez utiliser un nouveau lien de réinitialisation.');
      return;
    }

    const passwordError = validatePassword(password);
    if (passwordError) {
      setFormError(passwordError);
      return;
    }

    if (password !== confirmPassword) {
      setFormError('Les mots de passe ne correspondent pas');
      return;
    }

    setIsLoading(true);
    setFormError(null);

    try {
      const result = await updatePassword(password);

      if (!result.success) {
        const error = result.error as any;
        const errorCode = error?.code || error?.error_code;
        const errorMessage = error?.message || error?.msg || '';
        
        if (errorCode === 'same_password' || errorMessage.toLowerCase().includes('same password')) {
          setFormError('Le nouveau mot de passe doit être différent de l\'ancien');
        } else {
          setFormError('Erreur lors de la réinitialisation du mot de passe');
        }
        setIsLoading(false);
        return;
      }

      const userId = storedSession?.user?.id;
      if (userId) {
        await updateHasSignedUp(userId, true);
      }
      
      // Refresh the session to ensure the store is updated before navigation
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        setSession(session);
      }
      
      setIsLoading(false);
      
      // Navigate after session is refreshed
      navigate('/');
    } catch (error: any) {
      const errorCode = error?.code || error?.error_code;
      const errorMessage = error?.message || error?.msg || '';
      
      if (errorCode === 'same_password' || errorMessage.toLowerCase().includes('same password')) {
        setFormError('Le nouveau mot de passe doit être différent de l\'ancien');
      } else {
        setFormError('Erreur lors de la réinitialisation du mot de passe');
      }
      setIsLoading(false);
    }
  };

  // Show loading state while verifying
  if (isVerifying) {
    return (
      <div className="min-h-screen bg-black flex flex-col items-center justify-center p-6 relative overflow-hidden">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-warning/10 rounded-full blur-3xl animate-slow-pulse" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-warning/10 rounded-full blur-3xl animate-slow-pulse" style={{ animationDelay: '5s' }} />
        
        <div className="text-center w-full max-w-md mx-auto relative z-10">
          <img 
            src={logoImage} 
            alt="PRSNL" 
            className="w-40 h-auto mx-auto mb-8 brightness-0 invert"
          />
          <Loader2 className="w-12 h-12 text-warning animate-spin mx-auto mb-4" />
          <p className="text-white text-lg">Vérification du lien...</p>
          <p className="text-white/60 text-sm">Veuillez patienter</p>
        </div>
      </div>
    );
  }

  // Show error state if verification failed
  if (verificationError) {
    return <InvalidLinkScreen />;
  }

  // Show form only if session is valid
  if (!isSessionValid) {
    return null;
  }

  return (
    <div className="min-h-screen bg-black flex flex-col items-center justify-center p-6 relative overflow-hidden">
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-warning/10 rounded-full blur-3xl animate-slow-pulse" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-warning/10 rounded-full blur-3xl animate-slow-pulse" style={{ animationDelay: '5s' }} />

      <div className="text-center w-full max-w-md mx-auto relative z-10">
        <img 
          src={logoImage} 
          alt="PRSNL" 
          className="w-40 h-auto mx-auto mb-8 brightness-0 invert"
        />
        
        <div className="mb-8">
          <h1 className="text-2xl font-semibold text-white mb-2">
            Nouveau mot de passe
          </h1>
          <p className="text-white/60 text-sm">
            Choisissez un nouveau mot de passe sécurisé
          </p>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-3 text-left">
            <label htmlFor="password" className="text-white text-sm block mb-2">
              Nouveau mot de passe
            </label>
            <PasswordInput
              id="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setFormError(null);
              }}
              disabled={isLoading}
              className="w-full h-12 rounded-full bg-white/5 backdrop-blur-xl border border-white/10 text-white placeholder:text-white/30 focus:bg-white/10 focus:border-white/20 px-5 outline-none transition-all"
            />
          </div>

          <div className="space-y-3 text-left">
            <label htmlFor="confirmPassword" className="text-white text-sm block mb-2">
              Confirmer le mot de passe
            </label>
            <PasswordInput
              id="confirmPassword"
              placeholder="••••••••"
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                setFormError(null);
              }}
              disabled={isLoading}
              className="w-full h-12 rounded-full bg-white/5 backdrop-blur-xl border border-white/10 text-white placeholder:text-white/30 focus:bg-white/10 focus:border-white/20 px-5 outline-none transition-all"
            />
          </div>

          {formError && (
            <p className="text-red-400 text-sm text-center">{formError}</p>
          )}
          
          <Button
            type="submit"
            disabled={isLoading || !isSessionValid}
            className="w-full h-12 rounded-full bg-white text-black hover:bg-white/90 font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Réinitialisation...' : 'Réinitialiser le mot de passe'}
          </Button>
        </form>
      </div>
    </div>
  );
}

function InvalidLinkScreen() {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const handleRequestNewLink = async () => {
    if (!email?.includes('@')) {
      toast.error('Veuillez entrer une adresse email valide');
      return;
    }

    setIsLoading(true);
    try {
      const result = await resetPasswordForEmail(email, globalThis.location.origin);
      if (result.success) {
        setEmailSent(true);
        toast.success('Un nouveau lien a été envoyé à votre email');
      } else {
        toast.error('Erreur lors de l\'envoi. Veuillez réessayer.');
      }
    } catch {
      toast.error('Erreur lors de l\'envoi. Veuillez réessayer.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black flex flex-col items-center justify-center p-6 relative overflow-hidden">
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-warning/10 rounded-full blur-3xl animate-slow-pulse" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-warning/10 rounded-full blur-3xl animate-slow-pulse" style={{ animationDelay: '5s' }} />
      
      <Link
        to="/login"
        className="absolute top-6 left-6 z-20 p-2 text-white/70 hover:text-white transition-colors"
        aria-label="Retour"
      >
        <ArrowLeft className="w-6 h-6" />
      </Link>
      
      <div className="text-center w-full max-w-md mx-auto relative z-10">
        <img 
          src={logoImage} 
          alt="PRSNL" 
          className="w-40 h-auto mx-auto mb-8 brightness-0 invert"
        />
        
        {emailSent ? (
          <div className="mb-8">
            <h1 className="text-2xl font-semibold text-white mb-2">
              Email envoyé !
            </h1>
            <p className="text-white/60 text-sm mb-6">
              Un nouveau lien de réinitialisation a été envoyé à {email}
            </p>
            <Button
              asChild
              className="w-full h-12 rounded-full bg-white text-black hover:bg-white/90 font-medium transition-all"
            >
              <Link to="/login">Retour à la connexion</Link>
            </Button>
          </div>
        ) : (
          <div className="mb-8">
            <h1 className="text-2xl font-semibold text-white mb-2">
              Lien invalide
            </h1>
            <p className="text-white/60 text-sm mb-6">
              Le lien de réinitialisation est invalide ou a expiré. Entrez votre email pour recevoir un nouveau lien.
            </p>
            
            <div className="space-y-4">
              <Input
                type="email"
                placeholder="votre@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full h-12 rounded-full bg-white/5 backdrop-blur-xl border border-white/10 text-white placeholder:text-white/30 focus:bg-white/10 focus:border-white/20 px-5 outline-none transition-all"
                disabled={isLoading}
              />
              
              <Button
                onClick={handleRequestNewLink}
                disabled={isLoading || !email}
                className="w-full h-12 rounded-full bg-white text-black hover:bg-white/90 font-medium transition-all disabled:opacity-50"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Envoi en cours...
                  </>
                ) : (
                  'Demander un nouveau lien'
                )}
              </Button>
              
              <Button
                variant="ghost"
                asChild
                className="w-full h-12 rounded-full text-white/70 hover:text-white hover:bg-white/5 font-medium transition-all"
              >
                <Link to="/login">Retour à la connexion</Link>
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
